import { useEffect, useId, useRef, useState } from 'react'
import { getCompanyLookupCopy } from './companyLookupCopy'
import './CompanyLookup.css'

export const EMPTY_COMPANY = { company: '', zefix: '', address: '', npa: '', city: '', canton: '' }
const CANTONS = 'AG AI AR BE BL BS FR GE GL GR JU LU NE NW OW SG SH SO SZ TG TI UR VD VS ZG ZH'.split(' ')
const formatUid = uid => String(uid || '').replace(/^CHE(\d{3})(\d{3})(\d{3})$/, 'CHE-$1.$2.$3')

export default function CompanyLookup({ value, onChange, lookup, lang = 'fr', disabled = false, allowSearch = true }) {
  const c = getCompanyLookupCopy(lang), id = useId()
  const [query, setQuery] = useState(''), [manual, setManual] = useState(!allowSearch || Boolean(value.company))
  const [selected, setSelected] = useState(null), [results, setResults] = useState([])
  const [phase, setPhase] = useState('idle'), [more, setMore] = useState(false), [active, setActive] = useState(-1)
  const request = useRef(null), generation = useRef(0)
  const cancel = () => { generation.current += 1; request.current?.abort() }

  useEffect(() => {
    if (!allowSearch || manual || selected || query.trim().length < 3) return
    const controller = new AbortController(), turn = ++generation.current
    request.current = controller
    const timer = setTimeout(async () => {
      setPhase('searching')
      try {
        const data = await lookup({ query: query.trim() }, { signal: controller.signal })
        if (controller.signal.aborted || turn !== generation.current) return
        if (!Array.isArray(data?.companies)) throw new Error('invalid_response')
        setResults(data.companies); setMore(Boolean(data.hasMore)); setPhase('results')
      } catch {
        if (!controller.signal.aborted && turn === generation.current) setPhase('error')
      }
    }, 400)
    return () => { clearTimeout(timer); controller.abort() }
  }, [query, manual, selected, lookup, allowSearch])
  useEffect(() => () => { request.current?.abort(); generation.current += 1 }, [])

  const updateQuery = text => {
    cancel(); setQuery(text); setResults([]); setSelected(null); setActive(-1); setMore(false); setPhase('idle')
    onChange({ ...EMPTY_COMPANY })
  }
  const choose = async result => {
    if (disabled) return
    cancel()
    const controller = new AbortController(), turn = generation.current
    request.current = controller
    setSelected(result); setResults([]); setActive(-1); setPhase('detail'); onChange({ ...EMPTY_COMPANY })
    try {
      const { company } = await lookup({ uid: result.uid }, { signal: controller.signal })
      if (controller.signal.aborted || turn !== generation.current) return
      if (!company?.name || company.uid !== result.uid) throw new Error('invalid_response')
      const fields = { company: company.name, zefix: formatUid(company.uid), address: company.address || '', npa: company.npa || '', city: company.city || '', canton: company.canton || '' }
      onChange(fields); setQuery(company.name)
      if (Object.values(fields).every(Boolean)) setPhase('filled')
      else { setManual(true); setPhase('incomplete') }
    } catch {
      if (!controller.signal.aborted && turn === generation.current) { setSelected(null); setPhase('error') }
    }
  }
  const startManual = () => { cancel(); setManual(true); setResults([]); setPhase('idle'); setSelected(null) }
  const reset = () => { cancel(); setManual(false); updateQuery('') }
  const summary = selected && phase === 'filled' && !manual
  const busy = ['searching', 'detail'].includes(phase)
  return <section className="hb-company-lookup" aria-label={c.label}>
    {!manual && <><label htmlFor={`${id}-query`}>{c.label}</label>
      <input id={`${id}-query`} role="combobox" autoComplete="off" value={query} maxLength={120} disabled={disabled}
        aria-autocomplete="list" aria-expanded={results.length > 0} aria-controls={`${id}-results`}
        aria-activedescendant={active >= 0 && results[active] ? `${id}-option-${active}` : undefined}
        aria-describedby={`${id}-hint`} placeholder={c.placeholder} onChange={e => updateQuery(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'ArrowDown' && results.length) { e.preventDefault(); setActive(i => (i + 1) % results.length) }
          if (e.key === 'ArrowUp' && results.length) { e.preventDefault(); setActive(i => (i <= 0 ? results.length : i) - 1) }
          if (e.key === 'Enter') { e.preventDefault(); if (results[active]) choose(results[active]) }
          if (e.key === 'Escape') { cancel(); setResults([]); setPhase('idle'); setActive(-1) }
        }} />
      <p id={`${id}-hint`} className="hb-lookup-hint">{c.hint}</p></>}
    <div aria-live="polite" aria-atomic="true">
      {busy && <p className="hb-lookup-message">{phase === 'detail' ? c.detail : c.loading}</p>}
      {phase === 'error' && <p className="hb-lookup-message hb-lookup-warning">{c.unavailable}</p>}
      {phase === 'results' && !results.length && <p className="hb-lookup-message">{c.none}</p>}
      {phase === 'incomplete' && <p className="hb-lookup-message hb-lookup-warning">{c.missing}</p>}
    </div>
    {!!results.length && <ul id={`${id}-results`} role="listbox" aria-label={c.results} className="hb-lookup-results">
      {results.map((result, index) => <li key={result.uid} id={`${id}-option-${index}`} role="option" aria-selected={active === index}
        tabIndex={disabled ? -1 : 0} aria-disabled={disabled} onClick={() => choose(result)} onKeyDown={e => { if (['Enter', ' '].includes(e.key)) { e.preventDefault(); choose(result) } }}>
        <strong>{result.name}</strong><span>{[result.city, result.canton].filter(Boolean).join(', ')} · {formatUid(result.uid)}</span>
      </li>)}
    </ul>}
    {more && !!results.length && <p className="hb-lookup-hint">{c.more}</p>}
    {summary && <div className="hb-lookup-summary"><p className="hb-lookup-caption">{c.filled}</p><strong>{value.company}</strong>
      <p>{value.zefix}<br/>{value.address}<br/>{value.npa} {value.city} · {value.canton}</p><small>{c.review}</small>
      <button type="button" className="hb-lookup-link" onClick={startManual} disabled={disabled}>{c.edit}</button>
    </div>}
    {manual && <div className="hb-lookup-manual">
      {[['company', c.company], ['zefix', c.uid], ['address', c.address], ['npa', c.npa], ['city', c.city]].map(([key, label]) =>
        <label key={key}>{label} *<input value={value[key]} onChange={e => onChange({ ...value, [key]: e.target.value })} disabled={disabled} inputMode={key === 'npa' ? 'numeric' : undefined}/></label>)}
      <label>{c.canton} *<select value={value.canton} onChange={e => onChange({ ...value, canton: e.target.value })} disabled={disabled}>
        <option value="">—</option>{CANTONS.map(canton => <option key={canton}>{canton}</option>)}
      </select></label>
    </div>}
    {allowSearch && (manual ? <button type="button" className="hb-lookup-link" onClick={reset} disabled={disabled}>{c.searchAgain}</button>
      : !summary && <button type="button" className="hb-lookup-link" onClick={startManual} disabled={disabled}>{c.manual}</button>)}
  </section>
}
